import React from "react";
import { projectsApi } from "../api/projects";

const ProjectsContext = React.createContext(null);
const ACTIVE_PROJECT_KEY = "webprofit_active_project_slug";

export function ProjectsProvider({ children }) {
  const [projects, setProjects] = React.useState([]);
  const [activeProject, setActiveProjectState] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const loadProjects = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await projectsApi.list();
      const list = data.projects || [];
      setProjects(list);

      const storedSlug = localStorage.getItem(ACTIVE_PROJECT_KEY);
      const storedProject = list.find((project) => project.slug === storedSlug);
      const nextActive = storedProject || list[0] || null;
      setActiveProjectState(nextActive);
      if (nextActive?.slug) localStorage.setItem(ACTIVE_PROJECT_KEY, nextActive.slug);

      return list;
    } catch (err) {
      setProjects([]);
      setActiveProjectState(null);
      setError(err.message || "No se pudieron cargar los proyectos");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const setActiveProject = React.useCallback((projectOrSlug) => {
    setActiveProjectState((current) => {
      const project =
        typeof projectOrSlug === "string"
          ? projects.find((item) => item.slug === projectOrSlug || item.id === projectOrSlug)
          : projectOrSlug;

      if (project?.slug) localStorage.setItem(ACTIVE_PROJECT_KEY, project.slug);
      return project || current;
    });
  }, [projects]);


  React.useEffect(() => {
    const handleBillingRestriction = (event) => {
      const detail = event?.detail || {};
      const path = String(detail.path || "");
      const match = path.match(/\/projects\/([^/?#]+)/);
      const projectKey = match ? decodeURIComponent(match[1]) : "";
      const access = detail.access || {};

      const applyRestriction = (project) => {
        if (!project) return project;
        const matches =
          !projectKey ||
          project.slug === projectKey ||
          project.id === projectKey ||
          project._id === projectKey;
        if (!matches) return project;

        return {
          ...project,
          billing: {
            ...(project.billing || {}),
            status: project.billing?.status || "past_due",
            accessMode: access.mode || "landing_only",
            graceUntil: access.graceUntil || project.billing?.graceUntil || null,
            restrictedAt: access.restrictedAt || project.billing?.restrictedAt || new Date().toISOString(),
          },
        };
      };

      setProjects((current) => current.map(applyRestriction));
      setActiveProjectState((current) => applyRestriction(current));
    };

    window.addEventListener("webprofit:billing-restricted", handleBillingRestriction);
    return () => window.removeEventListener("webprofit:billing-restricted", handleBillingRestriction);
  }, []);

  const createProject = async (payload) => {
    setError("");
    const data = await projectsApi.create(payload);
    await loadProjects();
    if (data.project) setActiveProject(data.project);
    return data.project;
  };

  const value = React.useMemo(
    () => ({
      projects,
      activeProject,
      loading,
      error,
      loadProjects,
      createProject,
      setActiveProject,
    }),
    [projects, activeProject, loading, error, loadProjects, setActiveProject]
  );

  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>;
}

export function useProjects() {
  const context = React.useContext(ProjectsContext);
  if (!context) throw new Error("useProjects debe usarse dentro de ProjectsProvider");
  return context;
}
